// Account-First Phase 3 — shared helpers for translating a portal
// account's polymorphic attachments into the composer's OwnerSelection
// shape. `LegalEntity` is the canonical wire name on
// PortalUserAttachment.EntityType; the composer's OwnerPicker uses
// "Entity" for the same concept. Attachment types outside the picker's
// known set (e.g. ExternalRecipient) are skipped — staff can still pick
// them manually via OwnerPicker.

import type { PortalAccountAttachmentDto } from "@/api/portalAccounts";
import type {
  OwnerEntityType,
  OwnerSelection,
} from "@/components/Collection/OwnerPicker";

const COMPOSER_OWNER_TYPES = new Set<OwnerEntityType>([
  "Deal",
  "Person",
  "Entity",
  "Business",
  "Broker",
  "Vendor",
  "Loan",
]);

export function attachmentToOwnerType(
  entityType: string,
): OwnerEntityType | null {
  if (entityType === "LegalEntity") return "Entity";
  if (COMPOSER_OWNER_TYPES.has(entityType as OwnerEntityType)) {
    return entityType as OwnerEntityType;
  }
  return null;
}

export function attachmentToOwner(
  attachment: PortalAccountAttachmentDto,
): OwnerSelection | null {
  const t = attachmentToOwnerType(attachment.entityType);
  if (!t) return null;
  return {
    ownerEntityType: t,
    ownerEntityId: attachment.entityId,
    ownerDisplayName:
      attachment.notes?.trim() || `${t} ${attachment.entityId.slice(0, 8)}`,
  };
}

/**
 * First composer-compatible attachment, or null. Used to pre-fill the
 * Filing detail section on the account-first composer so staff don't
 * have to think about Owner unless they want to override the default.
 */
export function firstCompatibleOwner(
  attachments: PortalAccountAttachmentDto[],
): OwnerSelection | null {
  for (const a of attachments) {
    const owner = attachmentToOwner(a);
    if (owner) return owner;
  }
  return null;
}
